import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { API_END_POINTS } from "../ApiEndPoints";
import { BASE_URL } from "../ApiEndPoints";
import axios from "axios";
import globalFunction from "../../utils/globalFunction";

const initialState = {
  isLoading: false,
  isError: false,
  message: null,
};

const handleUnauthorizedError = (message) => {
  if (
    message === "Unauthorized" ||
    message?.toLowerCase().includes("user not authorized") ||
    message?.toLowerCase().includes("token has expired") ||
    message?.toLowerCase().includes("jwt expire")
  ) {
    globalFunction.logoutFunction();
  }
};

export const courseCreationAPI = createAsyncThunk(
  "courseCreationAPI",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        API_END_POINTS.CourseCreation,
        credentials.payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${credentials.token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        handleUnauthorizedError(error?.response?.data?.message);

        return rejectWithValue(
          error.response.data.message || "Invalid credentials"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

export const courseInformationStepAPI = createAsyncThunk(
  "courseInformationStepAPI",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        BASE_URL + `course/${credentials.courseId}/courseInformation`,
        credentials.payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${credentials.token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        handleUnauthorizedError(error?.response?.data?.message);

        return rejectWithValue(
          error.response.data.message || "Invalid credentials"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

export const CourseOptionAPI = createAsyncThunk(
  "CourseOptionAPI",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.get(API_END_POINTS.CourseOption, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${credentials.token}`,
        },
      });

      return response.data;
    } catch (error) {
      if (error.response) {
        handleUnauthorizedError(error?.response?.data?.message);

        return rejectWithValue(
          error.response.data.message || "Invalid credentials"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

export const courseInterfaceStepAPI = createAsyncThunk(
  "courseInterfaceStepAPI",
  async (credentials, { rejectWithValue }) => {
    try {
      let PrevousImages = [];
      let PrevousVideos = [];

      if (credentials?.payload?.images?.length > 0) {
        if (typeof credentials?.payload?.images === "string") {
          PrevousImages = JSON.parse(credentials?.payload?.images)?.filter(
            (item) => typeof item === "string"
          );
        } else {
          // Filter for existing images (either strings or objects with isExisting flag)
          PrevousImages = credentials?.payload?.images?.filter(
            (item) => typeof item === "string" || item.isExisting
          );
        }
      }

      if (credentials?.payload?.videos?.length > 0) {
        if (typeof credentials?.payload?.videos === "string") {
          PrevousVideos = JSON.parse(credentials?.payload?.videos)?.filter(
            (item) => typeof item === "string"
          );
        } else {
          // Filter for existing videos (either strings or objects with isExisting flag)
          PrevousVideos = credentials?.payload?.videos?.filter(
            (item) => typeof item === "string" || item.isExisting
          );
        }
      }

      const formData = new FormData();
      // Append non-file fields

      // Extract URLs from existing images/videos
      const existingImageUrls = (PrevousImages || []).map(item => {
        if (typeof item === 'string') return item;
        return item.previewUrl?.replace(process.env.REACT_APP_GALLERY_URL, '') || item.url;
      });
      
      const existingVideoUrls = (PrevousVideos || []).map(item => {
        if (typeof item === 'string') return item;
        return item.previewUrl?.replace(process.env.REACT_APP_GALLERY_URL, '') || item.url;
      });

      formData.append(
        "existing_image_urls",
        JSON.stringify(existingImageUrls)
      );
      formData.append(
        "existing_promo_video_urls",
        JSON.stringify(existingVideoUrls)
      );

      formData.append("subtitle", credentials?.payload?.subtitle || "");
      formData.append("category_id", credentials?.payload?.category || "");
      formData.append(
        "basic_info_option_id",
        credentials?.payload?.basic_info_option_id || ""
      );
      formData.append(
        "subcategory_id",
        credentials?.payload?.subcategory || ""
      );
      formData.append("level_id", credentials?.payload?.level || "");
      formData.append("primary_taught", credentials?.payload?.taught || "");
      // Append image files
      if (
        credentials?.payload?.images &&
        Array.isArray(credentials.payload.images)
      ) {
        const validImages = credentials.payload.images.filter(
          (image) => image.file instanceof File && !image.isUploaded
        );
        if (validImages.length > 10) {
          return rejectWithValue("Cannot upload more than 10 images");
        }
        validImages.forEach((image) => {
          console.log("Appending image:", image.name); // Debug: Confirm image upload
          formData.append("image", image.file, image.name);
        });
      } else {
        console.log("No valid images found in payload"); // Debug: Check if images are missing
      }
      // Append video files
      if (
        credentials?.payload?.videos &&
        Array.isArray(credentials.payload.videos)
      ) {
        const validVideos = credentials.payload.videos.filter(
          (video) => video.file instanceof File && !video.isUploaded
        );
        if (validVideos.length > 5) {
          return rejectWithValue("Cannot upload more than 5 videos");
        }
        validVideos.forEach((video) => {
          console.log("Appending video:", video.name); // Debug: Confirm video upload
          formData.append("promo_video", video.file, video.name);
        });
      } else {
        console.log("No valid videos found in payload"); // Debug: Check if videos are missing
      }
      // Log FormData contents for debugging
      for (let [key, value] of formData.entries()) {
        console.log(
          `FormData key: ${key}, value: ${
            value instanceof File ? `File: ${value.name}` : value
          }`
        );
      }
      const response = await axios.post(
        BASE_URL + `course/${credentials.courseId}/courseInterface`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${credentials.token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.log(error);
      if (error.response) {
        handleUnauthorizedError(error?.response?.data?.message);
        return rejectWithValue(
          error.response.data.message || "Invalid credentials"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

export const uploadDocumentsAPI = createAsyncThunk(
  "uploadDocumentsAPI",
  async (credentials, { rejectWithValue }) => {
    try {
      console.log("Upload Documents API called with:", {
        courseId: credentials.courseId,
        documentsCount: credentials.payload?.documents?.length || 0,
        baseUrl: BASE_URL
      });

      const formData = new FormData();
      console.log("credentials.payload", credentials.payload.documents);
      
      // Check if documents exist and are valid
      if (!credentials?.payload?.documents || !Array.isArray(credentials.payload.documents) || credentials.payload.documents.length === 0) {
        console.log("No documents provided");
        return rejectWithValue("No documents provided for upload");
      }

      // Filter valid documents (only files that are File instances)
      const validDocuments = credentials.payload.documents.filter(
        (doc) => doc.file instanceof File
      );
      console.log("validDocuments", validDocuments);
      
      if (validDocuments.length === 0) {
        console.log("No valid documents found");
        return rejectWithValue("No valid documents to upload");
      }

      if (validDocuments.length > 10) {
        return rejectWithValue("Cannot upload more than 10 documents");
      }

      // Append document files
      validDocuments.forEach((doc) => {
        console.log("Appending document:", doc.name, "File type:", doc.file.type, "File size:", doc.file.size);
        formData.append("documents", doc.file, doc.name);
      });

      // Log FormData contents for debugging
      console.log("FormData entries:");
      for (let [key, value] of formData.entries()) {
        console.log(
          `FormData key: ${key}, value: ${
            value instanceof File ? `File: ${value.name}` : value
          }`
        );
      }

      const uploadUrl = `${BASE_URL}course/${credentials.courseId}/uploadDocuments`;
      console.log("Uploading to URL:", uploadUrl);

      const response = await axios.post(
        uploadUrl,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${credentials.token}`,
          },
        }
      );

      console.log("Upload response:", response.data);
      return response.data;
    } catch (error) {
      console.log("Upload error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        config: error.config?.url
      });
      
      if (error.response) {
        handleUnauthorizedError(error?.response?.data?.message);
        return rejectWithValue(
          error.response.data.message || "Failed to upload documents"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

export const updateDocumentsAPI = createAsyncThunk(
  "updateDocumentsAPI",
  async (credentials, { rejectWithValue }) => {
    try {
      console.log("Update Documents API called with:", {
        courseId: credentials.courseId,
        documentsCount: credentials.payload?.documents?.length || 0,
        baseUrl: BASE_URL
      });

      const updateUrl = `${BASE_URL}course/${credentials.courseId}/updateDocuments`;
      console.log("Updating documents at URL:", updateUrl);

      const response = await axios.put(
        updateUrl,
        { documents: credentials.payload.documents },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${credentials.token}`,
          },
        }
      );

      console.log("Update response:", response.data);
      return response.data;
    } catch (error) {
      console.log("Update error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        config: error.config?.url
      });
      
      if (error.response) {
        handleUnauthorizedError(error?.response?.data?.message);
        return rejectWithValue(
          error.response.data.message || "Failed to update documents"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

export const learningObjectivesStepAPI = createAsyncThunk(
  "learningObjectivesStepAPI",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        BASE_URL + `course/${credentials.courseId}/learningObjectives`,
        credentials.payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${credentials.token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        handleUnauthorizedError(error?.response?.data?.message);

        return rejectWithValue(
          error.response.data.message || "Invalid credentials"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

export const addUnitStepAPI = createAsyncThunk(
  "addUnitStepAPI",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        BASE_URL + `course/${credentials.courseId}/addUnits`,
        credentials.payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${credentials.token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        handleUnauthorizedError(error?.response?.data?.message);

        return rejectWithValue(
          error.response.data.message || "Invalid credentials"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

export const deleteUnitAPI = createAsyncThunk(
  "deleteUnitAPI",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.delete(
        BASE_URL + `course/unit/${credentials.unit_id}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${credentials.token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        // handleUnauthorizedError(error?.response?.data?.message);

        return rejectWithValue(
          error.response.data.message || "Invalid credentials"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

export const getCourseByIdForCurriculumAPI = createAsyncThunk(
  "getCourseByIdForCurriculumAPI",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        BASE_URL + `course/${credentials.courseId}/getCoursebyID`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${credentials.token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        handleUnauthorizedError(error?.response?.data?.message);

        return rejectWithValue(
          error.response.data.message || "Invalid credentials"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

export const getCourseByIdForCourses = createAsyncThunk(
  "getCourseByIdForCourses",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        BASE_URL + `course/${credentials.courseId}/getCoursebyID`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${credentials.token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        handleUnauthorizedError(error?.response?.data?.message);

        return rejectWithValue(
          error.response.data.message || "Invalid credentials"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

export const getCourseByIdForStudentCourse = createAsyncThunk(
  "getCourseByIdForStudentCourse",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        BASE_URL + `student/courses/${credentials.courseId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${credentials.token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        handleUnauthorizedError(error?.response?.data?.message);

        return rejectWithValue(
          error.response.data.message || "Invalid credentials"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

export const getCourseByIdForAdminCourse = createAsyncThunk(
  "getCourseByIdForAdminCourse",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        BASE_URL + `admin/courses/${credentials.courseId}/details`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${credentials.token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        handleUnauthorizedError(error?.response?.data?.message);

        return rejectWithValue(
          error.response.data.message || "Invalid credentials"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

export const studentEnrolledCourse = createAsyncThunk(
  "studentEnrolledCourse",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        BASE_URL + `student/courses/${credentials.courseId}/enroll`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${credentials.token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        console.log(error);
        // handleUnauthorizedError(error?.response?.data?.message);

        return rejectWithValue(
          error.response.data.message || "Invalid credentials"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

export const studentUnenrolledCourse = createAsyncThunk(
  "studentUnenrolledCourse",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.delete(
        BASE_URL + `student/courses/${credentials.courseId}/enroll`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${credentials.token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        console.log(error);
        // handleUnauthorizedError(error?.response?.data?.message);

        return rejectWithValue(
          error.response.data.message || "Invalid credentials"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

export const enrolledCourseUnitApi = createAsyncThunk(
  "enrolledCourseUnitApi",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        API_END_POINTS.studentCourses +
          `/${credentials.courseId}/units/${credentials.unitId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${credentials.token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        handleUnauthorizedError(error?.response?.data?.message);

        return rejectWithValue(
          error.response.data.message || "Invalid credentials"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

export const adminEnrolledCourseUnitApi = createAsyncThunk(
  "adminEnrolledCourseUnitApi",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        API_END_POINTS.adminCourses +
          `/${credentials.courseId}/units/${credentials.unitId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${credentials.token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        handleUnauthorizedError(error?.response?.data?.message);

        return rejectWithValue(
          error.response.data.message || "Invalid credentials"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

export const createCurriculumAPI = createAsyncThunk(
  "createCurriculumAPI",
  async (credentials, { rejectWithValue }) => {
    let payload = {
      content_type: credentials.type,
    };
    try {
      const response = await axios.post(
        BASE_URL + `course/unit/${credentials?.c_id}/curriculum`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${credentials.token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        handleUnauthorizedError(error?.response?.data?.message);

        return rejectWithValue(
          error.response.data.message || "Invalid credentials"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);
export const deleteCurriculumAPI = createAsyncThunk(
  "deleteCurriculumAPI",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.delete(
        BASE_URL + `course/unit/curriculum/${credentials?.c_id}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${credentials.token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        handleUnauthorizedError(error?.response?.data?.message);

        return rejectWithValue(
          error.response.data.message || "Invalid credentials"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);
// video APIs
export const addVideoCurriculumAPI = createAsyncThunk(
  "createCurriculumAPI",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        BASE_URL + `course/unit/curriculum/${credentials?.curriculumId}/video`,
        credentials.data,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${credentials.token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        handleUnauthorizedError(error?.response?.data?.message);

        return rejectWithValue(
          error.response.data.message || "Invalid credentials"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

export const updateVideoCurriculumAPI = createAsyncThunk(
  "updateVideoCurriculumAPI",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.put(
        BASE_URL + `course/unit/curriculum/video/${credentials?.videoId}`,
        credentials.data,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${credentials.token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        handleUnauthorizedError(error?.response?.data?.message);

        return rejectWithValue(
          error.response.data.message || "Invalid credentials"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

export const deleteVideoCurriculumAPI = createAsyncThunk(
  "deleteVideoCurriculumAPI",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.delete(
        BASE_URL + `course/unit/curriculum/video/${credentials?.videoId}`,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${credentials.token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        handleUnauthorizedError(error?.response?.data?.message);

        return rejectWithValue(
          error.response.data.message || "Invalid credentials"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

// article APIs
export const addArticleCurriculumAPI = createAsyncThunk(
  "addArticleCurriculumAPI",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        BASE_URL +
          `course/unit/curriculum/${credentials?.curriculumId}/article`,
        { content: credentials.content, title: credentials.articleTitle },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${credentials.token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        handleUnauthorizedError(error?.response?.data?.message);

        return rejectWithValue(
          error.response.data.message || "Invalid credentials"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

export const updateArticleCurriculumAPI = createAsyncThunk(
  "updateArticleCurriculumAPI",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.put(
        BASE_URL + `course/unit/curriculum/article/${credentials?.articleId}`,
        { content: credentials.content, title: credentials.articleTitle },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${credentials.token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        handleUnauthorizedError(error?.response?.data?.message);

        return rejectWithValue(
          error.response.data.message || "Invalid credentials"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

export const deleteArticleCurriculumAPI = createAsyncThunk(
  "deleteArticleCurriculumAPI",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.delete(
        BASE_URL + `course/unit/curriculum/article/${credentials?.articleId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${credentials.token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        handleUnauthorizedError(error?.response?.data?.message);

        return rejectWithValue(
          error.response.data.message || "Invalid credentials"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

// Course Message
export const courseMessageForInstructorAPI = createAsyncThunk(
  "courseMessageForInstructorAPI",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        BASE_URL + `course/${credentials?.courseId}/courseMessage`,
        credentials.payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${credentials.token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        handleUnauthorizedError(error?.response?.data?.message);

        return rejectWithValue(
          error.response.data.message || "Invalid credentials"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

// Course Assignment (multiple_choice || short_answer || comprehensive)
export const addAssignmentCurriculumAPI = createAsyncThunk(
  "addAssignmentCurriculumAPI",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        BASE_URL +
          `course/unit/curriculum/${credentials?.curriculumId}/assignment`,
        credentials.payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${credentials.token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        handleUnauthorizedError(error?.response?.data?.message);

        return rejectWithValue(
          error.response.data.message || "Invalid credentials"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

export const updateAssignmentDataAPI = createAsyncThunk(
  "updateAssignmentDataAPI",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.put(
        BASE_URL +
          `course/unit/curriculum/assignment/${credentials?.assignment_id}`,
        credentials.payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${credentials.token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        handleUnauthorizedError(error?.response?.data?.message);

        return rejectWithValue(
          error.response.data.message || "Invalid credentials"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

export const editAssignmentDataAPI = createAsyncThunk(
  "editAssignmentDataAPI",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        BASE_URL +
          `course/unit/curriculum/assignment/${credentials?.assignment_id}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${credentials.token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        // handleUnauthorizedError(error?.response?.data?.message);

        return rejectWithValue(
          error.response.data.message || "Invalid credentials"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

// Course Add Assignment Question API
export const addAssignmentQuestionForMultipleChoiceAPI = createAsyncThunk(
  "addAssignmentQuestionForMultipleChoiceAPI",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        BASE_URL +
          `course/unit/curriculum/assignment/${credentials.assignmentId}/question`,
        credentials.payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${credentials.token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        console.log(error);
        // handleUnauthorizedError(error?.response?.data?.message);

        return rejectWithValue(
          error.response.data.message || "Invalid credentials"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

export const addAssignmentQuestionForShortQuestionAPI = createAsyncThunk(
  "addAssignmentQuestionForShortQuestionAPI",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        BASE_URL +
          `course/unit/curriculum/assignment/${credentials.assignmentId}/question`,
        credentials.payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${credentials.token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        console.log(error);
        // handleUnauthorizedError(error?.response?.data?.message);

        return rejectWithValue(
          error.response.data.message || "Invalid credentials"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

// Course Delete Assignment Question API
export const deleteAssignmentQuestionAPI = createAsyncThunk(
  "deleteAssignmentQuestionAPI",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.delete(
        BASE_URL +
          `course/unit/curriculum/assignment/question/${credentials.assignmentId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${credentials.token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        handleUnauthorizedError(error?.response?.data?.message);

        return rejectWithValue(
          error.response.data.message || "Invalid credentials"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

export const updateAssignmentQuestionAPI = createAsyncThunk(
  "updateAssignmentQuestionAPI",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.put(
        BASE_URL +
          `course/unit/curriculum/assignment/question/${credentials.assignmentId}`,
        credentials.payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${credentials.token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        // handleUnauthorizedError(error?.response?.data?.message);

        return rejectWithValue(
          error.response.data.message || "Invalid credentials"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

// Course Delete Assignment API
export const deleteAssignmentAPI = createAsyncThunk(
  "deleteAssignmentAPI",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.delete(
        BASE_URL +
          `course/unit/curriculum/assignment/${credentials.assignment_id}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${credentials.token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        handleUnauthorizedError(error?.response?.data?.message);

        return rejectWithValue(
          error.response.data.message || "Invalid credentials"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

// Course Delete Assignment Question API
export const getAssignmentQuestionAPI = createAsyncThunk(
  "getAssignmentQuestionAPI",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        BASE_URL +
          `course/unit/curriculum/assignment/${credentials.assignmentId}/questions`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${credentials.token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        handleUnauthorizedError(error?.response?.data?.message);

        return rejectWithValue(
          error.response.data.message || "Invalid credentials"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

// Comment Curriculum
export const addCommentToCurriculumApi = createAsyncThunk(
  "addCommentToCurriculumApi",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        API_END_POINTS.commonCurriculum + `/${credentials?.videoId}/comments`,
        { content: credentials?.content },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${credentials.token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        handleUnauthorizedError(error?.response?.data?.message);

        return rejectWithValue(
          error.response.data.message || "Invalid credentials"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

export const voteCommentApi = createAsyncThunk(
  "voteCommentApi",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        API_END_POINTS.commonComment + `/${credentials?.commentId}/vote`,
        { voteType: credentials?.voteType },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${credentials.token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        // handleUnauthorizedError(error?.response?.data?.message);

        return rejectWithValue(
          error.response.data.message || "Invalid credentials"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

export const commentFlagApi = createAsyncThunk(
  "commentFlagApi",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        API_END_POINTS.commonComment + `/${credentials?.commentId}/flag`,
        { voteType: credentials?.voteType },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${credentials.token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        handleUnauthorizedError(error?.response?.data?.message);

        return rejectWithValue(
          error.response.data.message || "Invalid credentials"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

export const commentRemoveApi = createAsyncThunk(
  "commentFlagApi",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.delete(
        API_END_POINTS.commonComment + `/${credentials?.commentId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${credentials.token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        handleUnauthorizedError(error?.response?.data?.message);

        return rejectWithValue(
          error.response.data.message || "Invalid credentials"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

export const commentReplyApi = createAsyncThunk(
  "commentFlagApi",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        API_END_POINTS.commonComment + `/${credentials?.commentId}/reply`,
        {"content": credentials?.comment},
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${credentials.token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        handleUnauthorizedError(error?.response?.data?.message);

        return rejectWithValue(
          error.response.data.message || "Invalid credentials"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

export const getAllCommentsApi = createAsyncThunk(
  "getAllCommentsApi",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        API_END_POINTS.studentVideos + `/${credentials?.videoId}/comments`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${credentials.token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        handleUnauthorizedError(error?.response?.data?.message);

        return rejectWithValue(
          error.response.data.message || "Invalid credentials"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

export const courseUnPublishAPI = createAsyncThunk(
  "course/unpublish",
  async ({ token, courseId, reason }, { rejectWithValue }) => {
    try {
      console.log("Thunk received:", { token, courseId, reason }); // Debug log
      const response = await axios.post(
        `${BASE_URL}course/${courseId}/unpublish`,
        { reason }, // Send only reason in the request body
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      console.log("API Payload:", { reason }); // Debug log
      return response.data;
    } catch (error) {
      console.error("API Error:", error); // Debug log
      if (error.response) {
        handleUnauthorizedError(error?.response?.data?.message);
        return rejectWithValue(
          error.response.data.message || "Invalid credentials"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

export const studentAssignmentStartAPI = createAsyncThunk(
  "studentAssignmentStartAPI",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        API_END_POINTS.studentAssignment +
          `/${credentials.assignment_Id}/start`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${credentials.token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        handleUnauthorizedError(error?.response?.data?.message);

        return rejectWithValue(
          error.response.data.message || "Invalid credentials"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

export const adminAssignmentAPI = createAsyncThunk(
  "adminAssignmentAPI",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        API_END_POINTS.adminAssignment +
          `/${credentials.assignment_Id}/view`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${credentials.token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        handleUnauthorizedError(error?.response?.data?.message);

        return rejectWithValue(
          error.response.data.message || "Invalid credentials"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

export const studentAssignmentSubmitAPI = createAsyncThunk(
  "studentAssignmentSubmitAPI",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        BASE_URL +
          "course/" +
          credentials?.course_id +
          "/assignments/" +
          credentials?.id +
          "/submitted-students",
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${credentials.token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        handleUnauthorizedError(error?.response?.data?.message);

        return rejectWithValue(
          error.response.data.message || "Invalid credentials"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

export const getStudentSubmittedAssignmentAPI = createAsyncThunk(
  "getStudentSubmittedAssignmentAPI",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        BASE_URL +
          "course/" +
          credentials?.course_id +
          "/assignments/" +
          credentials?.id +
          "/students/" +
          credentials?.student_id,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${credentials.token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        handleUnauthorizedError(error?.response?.data?.message);

        return rejectWithValue(
          error.response.data.message || "Invalid credentials"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

export const saveStudentSubmittedAssignmentAPI = createAsyncThunk(
  "saveStudentSubmittedAssignmentAPI",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        BASE_URL +
          "course/" +
          credentials?.course_id +
          "/assignments/" +
          credentials?.id +
          "/students/" +
          credentials?.student_id +
          "/mark",

        credentials?.data,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${credentials.token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        handleUnauthorizedError(error?.response?.data?.message);

        return rejectWithValue(
          error.response.data.message || "Invalid credentials"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

export const studentMultipleChoicesAPI = createAsyncThunk(
  "studentMultipleChoicesAPI",
  async (credentials, { rejectWithValue }) => {
    console.log(credentials.data);
    try {
      const response = await axios.post(
        API_END_POINTS.studentAssignment +
          `/${credentials.assignment_Id}/submit`,
        { answers: credentials.data },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${credentials.token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        // handleUnauthorizedError(error?.response?.data?.message);

        return rejectWithValue(
          error.response.data.message || "Invalid credentials"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

export const studentShortQuestionAPI = createAsyncThunk(
  "studentShortQuestionAPI",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        API_END_POINTS.studentAssignment +
          `/${credentials.assignment_Id}/submit`,
        { answers: credentials.data },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${credentials.token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        // handleUnauthorizedError(error?.response?.data?.message);

        return rejectWithValue(
          error.response.data.message || "Invalid credentials"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

export const studentAssignmentCompleteAPI = createAsyncThunk(
  "studentAssignmentCompleteAPI",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        API_END_POINTS.studentAssignment +
          `/${credentials.assignment_Id}/complete`,
        { answers: credentials.data },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${credentials.token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        // handleUnauthorizedError(error?.response?.data?.message);

        return rejectWithValue(
          error.response.data.message || "Invalid credentials"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

export const studentCourseCommentAndReviewAPI = createAsyncThunk(
  "studentCourseCommentAndReviewAPI",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        API_END_POINTS.studentCourses + `/${credentials.course_id}/review`,
        { comment: credentials?.comment, rating: credentials?.rating },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${credentials.token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        // handleUnauthorizedError(error?.response?.data?.message);

        return rejectWithValue(
          error.response.data.message || "Invalid credentials"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);

export const studentGradeAPI = createAsyncThunk(
  "studentGradeAPI",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        API_END_POINTS.studentCourses + `/${credentials.course_id}/grade`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${credentials.token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (error.response) {
        // handleUnauthorizedError(error?.response?.data?.message);

        return rejectWithValue(
          error.response.data.message || "Invalid credentials"
        );
      } else {
        return rejectWithValue("Something went wrong!");
      }
    }
  }
);



const courseSlice = createSlice({
  name: "course",
  initialState,
  reducers: {},
});

export default courseSlice.reducer;
